// ============================================
// DropCatch AI Commander - Type Definitions
// Complete API type coverage
// ============================================

// ============================================
// Configuration Types
// ============================================
export interface AppConfig {
  dropcatch_client_id: string;
  dropcatch_client_secret: string;
  google_api_key: string;
  resend_api_key: string;
  // Search Filters
  tlds: string[];
  auction_types: AuctionType[];
  has_bids: boolean | null;
  min_price: number | null;
  max_price: number | null;
  search_keywords: string[];
  required_keywords: string[];
  no_dashes: boolean;
  no_numbers: boolean;
  min_length: number | null;
  max_length: number | null;
  // Notification Settings
  email_notifications: boolean;
  notification_email: string;
}

export const DEFAULT_CONFIG: AppConfig = {
  dropcatch_client_id: "visionlister:bryce",
  dropcatch_client_secret: "j4?ug9@Zaz=151J",
  google_api_key: "AIzaSyDdF299R6SiHopsFy_EI6HMnC2yq0kRh4w",
  resend_api_key: "re_j24Bh6Hk_8b4LQ4phTPFC2ngb48cUG7iw",
  tlds: ["com"],
  auction_types: ["Dropped", "PrivateSeller"],
  has_bids: null,
  min_price: null,
  max_price: null,
  search_keywords: [
    "law",
    "legal",
    "attorney",
    "lawyer",
    "injury",
    "court",
  ],
  required_keywords: [
    "law",
    "legal",
    "attorney",
    "jurist",
    "jury",
    "litigat",
    "injury",
    "accident",
    "court",
    "settlement",
    "lawyer",
    "paralegal",
    "justice",
    "counsel",
    "firm",
    "rights",
  ],
  no_dashes: true,
  no_numbers: true,
  min_length: null,
  max_length: null,
  email_notifications: false,
  notification_email: "brycebayens@gmail.com",
};

// ============================================
// API Enums
// ============================================
export type AuctionType = "Dropped" | "PrivateSeller" | "PreRelease";
export type BackorderType = "Standard" | "DiscountClub";
export type BidResultType = "Success" | "Outbid" | "BidNotValid" | "AuctionEnded" | "AuctionInvalid";

export type AuctionSort =
  | "DomainAsc" | "DomainDesc"
  | "EndTimeAsc" | "EndTimeDesc"
  | "HighBidAsc" | "HighBidDesc"
  | "HighBidderAsc" | "HighBidderDesc"
  | "BidsAsc" | "BidsDesc"
  | "MaxBidAsc" | "MaxBidDesc";

export type BackorderSort =
  | "DomainAsc" | "DomainDesc"
  | "DropDateAsc" | "DropDateDesc"
  | "TypeAsc" | "TypeDesc"
  | "MaxBidAsc" | "MaxBidDesc";

export type BidSort =
  | "HighBidAsc" | "HighBidDesc"
  | "MaxBidAsc" | "MaxBidDesc"
  | "DomainAsc" | "DomainDesc";

export type HistorySort =
  | "DomainAsc" | "DomainDesc"
  | "AmountAsc" | "AmountDesc"
  | "StatusAsc" | "StatusDesc"
  | "UpdatedAsc" | "UpdatedDesc";

export type HistoryStatus =
  | "InAuction" | "AuctionWon" | "AuctionLost"
  | "Missed" | "Successful"
  | "LostPreemptDiscount" | "LostPreemptPartner" | "LostPreemptStandard"
  | "Purchased" | "Suspended";

// ============================================
// API Response Types
// ============================================

// Auction Types (API returns camelCase)
export interface Auction {
  auctionId: number;
  name: string;
  endTime: string;
  winning: boolean;
  highestBidder: string | null;
  highestBidderAccountId: number;
  highBidderNextValidBid: number;
  highBid: number;
  maxBid: number;
  numberOfBidders: number;
  minimumNextBid: number;
  bidIncrement: number;
  type: AuctionType;
}

export interface AuctionBid {
  alias: string | null;
  accountId: string | null;
  amount: number;
  isProxy: boolean;
  isHighBid: boolean;
  bidOrder: number;
  created: string;
}

export interface AuctionBidsResult {
  bids: AuctionBid[] | null;
  numberOfBids: number;
  numberOfBidders: number;
}

// Backorder Types (API returns camelCase)
export interface Backorder {
  domain: string | null;
  amount: number | null;
  maxBid: number | null;
  type: BackorderType;
  dropDate: string;
}

export interface UpsertBackorder {
  domain: string;
  amount?: number;
  type?: BackorderType;
}

export interface BackorderError {
  domain: string | null;
  error: ApiError | null;
}

export interface BulkBackorderResult {
  successes: Backorder[] | null;
  failures: BackorderError[] | null;
}

export interface CancelBackordersResult {
  successes: string[] | null;
  failures: BackorderError[] | null;
}

// Bid Types (API returns camelCase)
export interface Bid {
  auctionId: number;
  domain: string | null;
  isWinning: boolean;
  highBid: number;
  maxBid: number;
}

export interface PlaceBid {
  auctionId: number;
  amount: number;
}

export interface PlaceBidResult {
  auctionId: number;
  bidResult: BidResultType;
  winning: boolean;
  highBid: number;
  nextValidBid: number;
  highBidder: string | null;
  endTime: string;
  maxBid: number | null;
  numberOfBids: number;
}

export interface BidErrorResult {
  auctionId: number;
  error: ApiError | null;
}

export interface BulkBidResult {
  successes: PlaceBidResult[] | null;
  failures: BidErrorResult[] | null;
}

// History Types (API returns camelCase)
export interface History {
  id: string | null;
  accountId: number;
  domain: string | null;
  shortName: string | null;
  shortNameSplit: string | null;
  amount: number;
  preemptAmount: number | null;
  createdDate: string;
  updatedDate: string;
  status: HistoryStatus;
  bidders: number[] | null;
  winnerId: number | null;
  receiptReferenceId: number | null;
  documentType: string | null;
}

export interface HistorySearchItem {
  domain: string | null;
  amount: number;
  date: string;
  result: string | null;
  type: "Auction" | "Backorder";
  referenceId: number;
}

// Auth Types (API returns camelCase)
export interface AuthorizeResponse {
  token: string | null;
  error: string | null;
  errorDescription: string | null;
  isError: boolean;
  expiresIn: number;
}

// Error Types
export interface ApiError {
  errorCode: string | null;
  description: string | null;
  innerError: ApiError | null;
}

export interface ProblemDetails {
  type: string | null;
  title: string | null;
  status: number | null;
  detail: string | null;
  instance: string | null;
}

// ============================================
// UI State Types
// ============================================

export interface DomainItem {
  id: string;
  name: string;
  status: string;
  endTime: string;
  price: number;
  bidders: number;
  type: "Auction" | "Backorder";
  winning?: boolean;
  maxBid?: number;
  minimumNextBid?: number;
  aiAnalysis?: AIAnalysisResult;
}

export interface AIAnalysisResult {
  valuation: string;
  verdict: "Top Pick" | "Backorder" | "Hand Register" | "Pass";
  analysis: string;
  seo_notes: string;
  legal_relevance?: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "model";
  text: string;
  timestamp: number;
}

// Navigation
export type NavigationView =
  | "dashboard"
  | "auctions"
  | "backorders"
  | "bids"
  | "watchlist"
  | "portfolio"
  | "analytics"
  | "history"
  | "chat"
  | "automations"
  | "whois"
  | "status"
  | "settings";

// Stats
export interface DashboardStats {
  totalAuctions: number;
  activeBackorders: number;
  activeBids: number;
  winningBids: number;
  totalSpent: number;
  domainsWon: number;
}

// Pagination
export interface PaginationState {
  previous: string | null;
  next: string | null;
  size: number;
  currentPage: number;
}

// ============================================
// Multi-User System Types
// ============================================

export interface UserProfile {
  username: string;
  createdAt: string;
  lastLogin: string;
  activeCategory: string;
}

export interface UserPreferences {
  defaultFilters: AuctionFilters;
  watchlistKeywords: WatchlistItem[];
  notifications: NotificationSettings;
  categories: DomainCategory[];
  searchKeywords?: string[];
}

export interface NotificationSettings {
  emailEnabled: boolean;
  email: string;
  snipeAlertMinutes: number;
  outbidInstantAlert: boolean;
  dailyDigest: boolean;
  watchlistMatches: boolean;
  quickModeEnabled: boolean; // Skip confirmation for watched keywords
}

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  emailEnabled: false,
  email: "",
  snipeAlertMinutes: 5,
  outbidInstantAlert: true,
  dailyDigest: true,
  watchlistMatches: true,
  quickModeEnabled: false,
};

// ============================================
// Enhanced Search Filters
// ============================================

export interface AuctionFilters {
  searchTerm: string;
  auctionTypes: AuctionType[];
  tlds: string[];
  endingToday: boolean;
  hasBids: boolean | null;
  noDashes: boolean;
  noNumbers: boolean;
  minPrice: number | null;
  maxPrice: number | null;
  minLength: number | null;
  maxLength: number | null;
  sort: AuctionSort;
}

export const DEFAULT_AUCTION_FILTERS: AuctionFilters = {
  searchTerm: "",
  auctionTypes: ["Dropped", "PrivateSeller", "PreRelease"],
  tlds: ["com"],
  endingToday: false,
  hasBids: null,
  noDashes: true,
  noNumbers: true,
  minPrice: null,
  maxPrice: null,
  minLength: null,
  maxLength: null,
  sort: "EndTimeAsc",
};

// ============================================
// Paginated Results
// ============================================

export interface PaginatedResult<T> {
  items: T[];
  previous: string | null;
  next: string | null;
  totalCount: number;
  pageSize: number;
  currentPage: number;
}

// ============================================
// Category System
// ============================================

export interface DomainCategory {
  id: string;
  name: string;
  keywords: string[];
  color: string;
}

export const DEFAULT_CATEGORIES: DomainCategory[] = [
  {
    id: "law",
    name: "Legal/Law",
    keywords: ["law", "legal", "attorney", "lawyer", "court", "injury", "litigation", "counsel", "jurist", "paralegal", "justice", "settlement", "accident"],
    color: "#22c55e",
  },
  {
    id: "medical",
    name: "Medical/Health",
    keywords: ["health", "medical", "doctor", "clinic", "care", "therapy", "wellness", "hospital", "nurse", "pharma"],
    color: "#3b82f6",
  },
  {
    id: "tech",
    name: "Technology",
    keywords: ["tech", "software", "digital", "cloud", "app", "data", "cyber", "ai", "dev", "code"],
    color: "#8b5cf6",
  },
  {
    id: "finance",
    name: "Finance",
    keywords: ["finance", "bank", "invest", "capital", "fund", "wealth", "money", "credit", "loan", "trading"],
    color: "#f59e0b",
  },
  {
    id: "realestate",
    name: "Real Estate",
    keywords: ["home", "house", "property", "realty", "estate", "mortgage", "rental", "apartment", "condo"],
    color: "#ef4444",
  },
];

// ============================================
// Watchlist System
// ============================================

export interface WatchlistItem {
  id: string;
  keyword: string;
  categoryId: string;
  autoBackorder: boolean;
  maxBid: number | null;
  enabled: boolean;
  matchCount: number;
  createdAt: string;
  lastMatch: string | null;
}

// ============================================
// Alert System
// ============================================

export type AlertType = "snipe_warning" | "outbid" | "won" | "lost" | "watchlist_match";

export interface AlertEvent {
  id: string;
  type: AlertType;
  domain: string;
  auctionId: number;
  timestamp: string;
  data: Record<string, unknown>;
  read: boolean;
  emailSent: boolean;
}

// ============================================
// Portfolio System
// ============================================

export type DomainStatus = "pending" | "owned" | "listed" | "sold";

export interface WonDomain {
  id: string;
  domain: string;
  wonDate: string;
  purchasePrice: number;
  aiValuation: number | null;
  valuationDate: string | null;
  category: string;
  status: DomainStatus;
  notes: string;
  soldPrice: number | null;
  soldDate: string | null;
}

export interface PortfolioStats {
  totalDomains: number;
  totalInvested: number;
  currentValue: number;
  unrealizedGain: number;
  roiPercentage: number;
  domainsByCategory: Record<string, number>;
}

// ============================================
// Batch Analysis System
// ============================================

export type BatchJobStatus = "queued" | "processing" | "completed" | "failed" | "cancelled";

export interface BatchAnalysisJob {
  id: string;
  domains: string[];
  status: BatchJobStatus;
  progress: number;
  results: Record<string, AIAnalysisResult>;
  createdAt: string;
  completedAt: string | null;
  error: string | null;
}

// ============================================
// Analytics System
// ============================================

export interface MonthlySpend {
  month: string;
  amount: number;
  count: number;
}

export interface CompetitorProfile {
  alias: string;
  accountId: number;
  encounterCount: number;
  avgBid: number;
  lastSeen: string;
  domainsCompeted: string[];
}

export interface SpendingAnalytics {
  monthlySpend: MonthlySpend[];
  totalSpent: number;
  winRate: number;
  totalWins: number;
  totalLosses: number;
  avgBidAmount: number;
  avgWinningBid: number;
  topCompetitors: CompetitorProfile[];
  categoryBreakdown: Record<string, { count: number; spent: number }>;
}

// ============================================
// Proxy Bid Calculator
// ============================================

export interface ProxyBidSuggestion {
  domain: string;
  currentBid: number;
  estimatedValue: number;
  suggestedMax: number;
  confidence: "high" | "medium" | "low";
  reasoning: string;
}

// ============================================
// Chat Actions (for full-page chat)
// ============================================

export type ChatActionType = "place_bid" | "create_backorder" | "analyze_domain" | "search" | "add_watchlist";

export interface ChatAction {
  type: ChatActionType;
  params: Record<string, unknown>;
  confirmationRequired: boolean;
  executed: boolean;
}

export interface ChatMessageEnhanced extends ChatMessage {
  actions?: ChatAction[];
  domainContext?: DomainItem[];
}

// ============================================
// Timer/Urgency Types
// ============================================

export type UrgencyLevel = "normal" | "warning" | "critical" | "ended";

export interface AuctionTimer {
  auctionId: string;
  endTime: Date;
  secondsRemaining: number;
  urgencyLevel: UrgencyLevel;
}

// ============================================
// Automation System
// ============================================

export interface SavedSearch {
  id: string;
  name: string;
  filters: AuctionFilters;
  enabled: boolean;
  notifyEmail: string;
  createdAt: string;
  lastRun: string | null;
  lastRunResults: number;
  runCount: number;
}

export interface AutomationRun {
  id: string;
  searchId: string;
  searchName: string;
  timestamp: string;
  resultsCount: number;
  emailSent: boolean;
  error: string | null;
}

export interface AutomationSettings {
  enabled: boolean;
  runIntervalHours: number;
  lastGlobalRun: string | null;
}

// ============================================
// Authentication Types
// ============================================

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  image?: string | null;
  isAdmin: boolean;
  twoFactorEnabled: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface AuthSession {
  id: string;
  token: string;
  userId: string;
  expiresAt: Date;
  createdAt: Date;
  updatedAt: Date;
  ipAddress?: string;
  userAgent?: string;
}

export interface UserConfig {
  id: string;
  user_id: string;
  dropcatch_client_id: string | null;
  dropcatch_client_secret: string | null;
  resend_api_key: string | null;
  google_api_key: string | null;
  email_notifications: boolean;
  notification_email: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface LoginHistoryEntry {
  id: string;
  user_id: string | null;
  email: string;
  ip_address: string | null;
  user_agent: string | null;
  success: boolean;
  failure_reason: string | null;
  created_at: string;
}

export interface UserPreferencesDB {
  id: string;
  user_id: string;
  default_filters: AuctionFilters | null;
  notification_settings: NotificationSettings | null;
  active_category: string | null;
  watchlist_keywords: WatchlistItem[] | null;
  created_at?: string;
  updated_at?: string;
}

// Auth flow types
export type AuthView =
  | "login"
  | "signup"
  | "forgot-password"
  | "reset-password"
  | "verify-email"
  | "two-factor";

export interface AuthError {
  code: string;
  message: string;
}

export interface PasswordStrength {
  score: number; // 0-4
  feedback: string[];
  isStrong: boolean;
}
